import './resolve.scss';

import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Modal } from '../../../components';
import { RESOLUTION_CONFIG } from '../../../config';
import { useUserContext } from '../../../context/user';
import {
  useAcceptDisputeMutation,
  useCreateRefundMutation,
  useRefundPreviewMutation
} from '../../../hooks/mutations/refunds';
import { AcceptDetailStep } from './accept-detail-step';
import { RefundDetailsStep } from './refund-details-step';
import { RefundSummaryStep } from './refund-summary-step';
import { ResolutionChooseStep } from './resolution-choose-step';
import { StepBack } from './step-back';
import { StepHeader } from './step-header';
import { SuccessStep } from './success-step';
import { TwoFactorStep } from './two-factor-step';

const STAGE = {
  CHOOSE: 'CHOOSE',
  DETAILS: 'REFUND_DETAILS',
  SUMMARY: 'REFUND_SUMMARY',
  ACCEPT_DETAIL: 'ACCEPT_DETAIL',
  TWO_FA: 'TWO_FA',
  SUCCESS: 'SUCCESS'
};

const makeIdempotencyKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `idem-${Math.random().toString(36).slice(2)}${Date.now()}`;

const readMessage = (error) =>
  error?.response?.data?.data?.message ||
  error?.response?.data?.message ||
  error?.message ||
  'Something went wrong, please try again.';

const hasEnabledTwoFactor = (user) =>
  Boolean(user?.twofa_enabled ?? user?.TwoFaEnabled ?? user?.has2FA);

const getTransactionReference = (dispute) => dispute?.transaction_reference || dispute?.reference;
const DEFAULT_REFUND_NETWORK = 'BEP20';

/**
 * Orchestrates the merchant dispute-resolution flow. Config-driven by dispute
 * type (OVERPAID / UNDERPAID) and the chosen option: refund options run
 * preview → summary → 2FA; accept options optionally show a confirmation
 * screen, then 2FA. 2FA is mandatory server-side.
 */
const ResolveDisputeModal = ({ dispute, onClose }) => {
  const { user } = useUserContext();
  const twofaEnabled = hasEnabledTwoFactor(user);

  const config = RESOLUTION_CONFIG[dispute?.dispute_type];
  const options = config?.options || [];

  const [stage, setStage] = useState(STAGE.CHOOSE);
  const [selectedId, setSelectedId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [wallet, setWallet] = useState({ wallet_address: '', wallet_network: '' });
  const [reason, setReason] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [successVariant, setSuccessVariant] = useState('refund');

  const idempotencyKey = useRef(makeIdempotencyKey());
  const previewRequestKey = useRef('');

  const { mutate: mutatePreview, isLoading: isPreviewing } = useRefundPreviewMutation();
  const createMutation = useCreateRefundMutation();
  const acceptMutation = useAcceptDisputeMutation();

  const option = options.find((entry) => entry.id === selectedId);
  const isSubmitting = createMutation.isLoading || acceptMutation.isLoading;
  const transactionReference = getTransactionReference(dispute);

  const handleSelect = (id) => {
    setSelectedId(id);
    setPreview(null);
    setPreviewError('');
    setWallet({ wallet_address: '', wallet_network: '' });
    previewRequestKey.current = '';
  };

  const goChooseNext = () => {
    if (!option) return;
    if (option.kind === 'refund') return setStage(STAGE.DETAILS);
    setStage(option.acceptHasDetail ? STAGE.ACCEPT_DETAIL : STAGE.TWO_FA);
  };

  const runPreview = useCallback(() => {
    setPreviewError('');
    mutatePreview(
      {
        reference: transactionReference,
        refund_type: option.value
      },
      {
        onSuccess: (res) => setPreview(res?.data ?? res),
        onError: (error) => {
          setPreview(null);
          setPreviewError(readMessage(error));
        }
      }
    );
  }, [mutatePreview, option?.value, transactionReference]);

  useEffect(() => {
    if (stage !== STAGE.DETAILS || option?.kind !== 'refund') return;

    const nextPreviewKey = `${transactionReference}:${option.value}`;
    if (previewRequestKey.current === nextPreviewKey) return;

    previewRequestKey.current = nextPreviewKey;
    runPreview();
  }, [runPreview, stage, option?.kind, option?.value, transactionReference]);

  const goReview = (values) => {
    setWallet({
      wallet_address: values?.wallet_address || '',
      wallet_network:
        values?.wallet_network ||
        preview?.wallet_network ||
        wallet.wallet_network ||
        DEFAULT_REFUND_NETWORK
    });
    setReason(values?.reason || '');
    setStage(STAGE.SUMMARY);
  };

  // A dispute type we don't resolve yet (e.g. LATE_PAYMENT) — render nothing.
  if (!config) return null;

  const confirmWithOtp = () => {
    setOtpError('');
    const onError = (error) => setOtpError(readMessage(error));

    if (option.kind === 'accept') {
      acceptMutation.mutate(
        {
          reference: dispute.reference,
          action: option.value,
          otp,
          idempotencyKey: idempotencyKey.current
        },
        {
          onSuccess: () => {
            setSuccessVariant('accept');
            setStage(STAGE.SUCCESS);
          },
          onError
        }
      );
      return;
    }

    createMutation.mutate(
      {
        reference: transactionReference,
        refund_type: option.value,
        wallet_address: wallet.wallet_address,
        wallet_network: wallet.wallet_network,
        reason,
        otp,
        idempotencyKey: idempotencyKey.current
      },
      {
        onSuccess: () => {
          setSuccessVariant('refund');
          setStage(STAGE.SUCCESS);
        },
        onError
      }
    );
  };

  const twoFaBack = () => {
    if (option?.kind !== 'accept') return setStage(STAGE.SUMMARY);
    setStage(option.acceptHasDetail ? STAGE.ACCEPT_DETAIL : STAGE.CHOOSE);
  };

  const back = {
    [STAGE.CHOOSE]: onClose,
    [STAGE.DETAILS]: () => setStage(STAGE.CHOOSE),
    [STAGE.SUMMARY]: () => setStage(STAGE.DETAILS),
    [STAGE.ACCEPT_DETAIL]: () => setStage(STAGE.CHOOSE),
    [STAGE.TWO_FA]: twoFaBack
  };

  const headers = {
    [STAGE.CHOOSE]: config.title,
    [STAGE.DETAILS]: option?.detailHeader,
    [STAGE.SUMMARY]: { name: 'Refund', cursive: 'summary' },
    [STAGE.ACCEPT_DETAIL]: option?.acceptHeader,
    [STAGE.TWO_FA]: { name: 'Two-factor', cursive: 'verification' }
  };

  return (
    <Modal onModalClose={onClose} ariaLabel="Resolve dispute">
      {stage !== STAGE.SUCCESS && headers[stage] && (
        <div className="resolve__top">
          <StepBack onBack={back[stage]} />
          <StepHeader name={headers[stage].name} cursive={headers[stage].cursive} />
        </div>
      )}
      {stage === STAGE.CHOOSE && (
        <ResolutionChooseStep
          dispute={dispute}
          options={options}
          prompt={config.prompt}
          selectedId={selectedId}
          onSelect={handleSelect}
          onNext={goChooseNext}
        />
      )}
      {stage === STAGE.DETAILS && (
        <RefundDetailsStep
          dispute={dispute}
          refundType={option.value}
          preview={preview}
          isPreviewing={isPreviewing}
          previewError={previewError}
          walletNetwork={preview?.wallet_network || wallet.wallet_network || DEFAULT_REFUND_NETWORK}
          onReview={goReview}
          onBack={back[STAGE.DETAILS]}
        />
      )}
      {stage === STAGE.SUMMARY && (
        <RefundSummaryStep
          dispute={dispute}
          refundType={option.value}
          preview={preview}
          walletAddress={wallet.wallet_address}
          walletNetwork={wallet.wallet_network}
          onContinue={() => setStage(STAGE.TWO_FA)}
          onBack={back[STAGE.SUMMARY]}
        />
      )}
      {stage === STAGE.ACCEPT_DETAIL && (
        <AcceptDetailStep
          dispute={dispute}
          onContinue={() => setStage(STAGE.TWO_FA)}
          onBack={back[STAGE.ACCEPT_DETAIL]}
        />
      )}
      {stage === STAGE.TWO_FA && (
        <TwoFactorStep
          twofaEnabled={twofaEnabled}
          otp={otp}
          onOtpChange={setOtp}
          onContinue={confirmWithOtp}
          onBack={back[STAGE.TWO_FA]}
          isSubmitting={isSubmitting}
          error={otpError}
        />
      )}
      {stage === STAGE.SUCCESS && <SuccessStep variant={successVariant} />}
    </Modal>
  );
};

ResolveDisputeModal.propTypes = {
  dispute: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};

export { ResolveDisputeModal };
